/**
 * NotificationBell — the unread badge and the recent-activity popover.
 *
 * Two queries, deliberately split: the count polls every 60s so the badge stays
 * live for a user who never opens the panel, while the list is `enabled` only
 * while the popover is open so a background tab is not paging notifications it
 * will never show. Marking everything read invalidates both.
 *
 * The count is capped at "99+" — past that the exact number tells nobody anything,
 * and an unbounded badge breaks the button's layout.
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Bell, Inbox } from 'lucide-react';
import type { NotificationDto, Paginated, UnreadCountResponse } from '@aforium/shared';
import { api } from '../../lib/apiClient';
import { queryKeys } from '../../lib/queryClient';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/ErrorState';
import { Popover } from '../ui/Popover';
import { Skeleton } from '../ui/Skeleton';
import { cn } from '../../lib/cn';

/** How many notifications the popover shows before deferring to the full page. */
const RECENT_COUNT = 8;
const POLL_INTERVAL_MS = 60_000;

const LIST_PARAMS = { pageSize: RECENT_COUNT } as const;

/** `2026-09-02T10:15:00.000Z` -> `about 3 hours ago`, defensively. */
function relativeTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return formatDistanceToNow(parsed, { addSuffix: true });
}

export interface NotificationBellProps {
  className?: string;
}

export function NotificationBell({ className }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const unreadQuery = useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: () => api.get<UnreadCountResponse>('/notifications/unread-count'),
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: POLL_INTERVAL_MS / 2,
  });

  const listQuery = useQuery({
    queryKey: queryKeys.notifications.list(LIST_PARAMS),
    queryFn: () => api.get<Paginated<NotificationDto>>('/notifications', { params: LIST_PARAMS }),
    enabled: isOpen,
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post<{ updated: number }>('/notifications/read', { all: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const close = useCallback(() => setIsOpen(false), []);

  const unread = unreadQuery.data?.unread ?? 0;
  const items = listQuery.data?.items ?? [];
  const hasItems = items.length > 0;
  // Trust the list too: the panel can open before the polled count has landed.
  const hasUnread = unread > 0 || items.some((notification) => notification.readAt === null);

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      align="end"
      label="Notifications"
      panelClassName="w-[20rem] overflow-hidden p-0 sm:w-[22rem]"
      className={cn('shrink-0', className)}
      trigger={
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          {unread > 0 ? (
            <span
              aria-hidden="true"
              className="absolute right-0.5 top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-2xs font-semibold leading-none text-danger-foreground"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </Button>
      }
    >
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-content">Notifications</h2>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          onClick={() => markAllRead.mutate()}
          isLoading={markAllRead.isPending}
          loadingLabel="Marking notifications read"
          disabled={!hasUnread}
        >
          Mark all read
        </Button>
      </div>

      <div className="scrollbar-thin max-h-80 overflow-y-auto">
        {listQuery.isPending ? (
          <div className="flex flex-col gap-3 p-4" role="status" aria-live="polite">
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex gap-3">
                <Skeleton className="h-2 w-2 shrink-0 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))}
            <span className="sr-only">Loading notifications</span>
          </div>
        ) : listQuery.error ? (
          <ErrorState
            error={listQuery.error}
            title="Could not load notifications"
            onRetry={() => void listQuery.refetch()}
            className="px-4 py-8"
          />
        ) : !hasItems ? (
          <EmptyState
            icon={Inbox}
            title="You are all caught up"
            description="New activity on your courses, submissions and tasks shows up here."
            className="px-4 py-8"
          />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((notification) => {
              const isUnread = notification.readAt === null;

              return (
                <li key={notification.id}>
                  <Link
                    to={notification.link ?? '/notifications'}
                    onClick={close}
                    className={cn(
                      'flex gap-3 px-4 py-3 transition-colors hover:bg-surface-sunken',
                      isUnread && 'bg-primary-soft/50',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                        isUnread ? 'bg-primary' : 'bg-transparent',
                      )}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-content">
                        {notification.title}
                        {isUnread ? <span className="sr-only"> (unread)</span> : null}
                      </span>
                      <span className="mt-0.5 line-clamp-2 text-sm text-content-muted">
                        {notification.body}
                      </span>
                      <time
                        dateTime={notification.createdAt}
                        className="mt-1 block text-xs text-content-subtle"
                      >
                        {relativeTime(notification.createdAt)}
                      </time>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="border-t border-border px-4 py-2.5">
        <Link
          to="/notifications"
          onClick={close}
          className="block rounded-lg py-1 text-center text-sm font-medium text-primary transition-colors hover:text-primary-hover"
        >
          View all notifications
        </Link>
      </div>
    </Popover>
  );
}
